import { FileType } from '@enums/FileType'
import { MimeType } from '@enums/MimeType'
import {
  GetMediaResponseDTO,
  GetMeteoReportDTO
} from '@models/api/GetMediaResponseDTO'
import { GetVariablePDFMediaResponseDTO } from '@models/api/GetVariablePDFMediaResponseDTO'
import { DownloadRequestParams } from '@models/downloads/DownloadRequestParams'
import { DownloadType } from '@models/downloads/DownloadType'
import { DownloadError } from '@models/error/DownloadError'
import { showToastError } from '@services/ToastService'
import { useDownloadsStore } from '@stores/downloadsStore'
import { AxiosResponse, HttpStatusCode } from 'axios'
import { newI18n } from 'src/i18n'

export class DownloadHelper {
  static getFileName(response: AxiosResponse) {
    const contentDisp =
      (response.headers['content-disposition'] as string) ||
      (response.headers['Content-Disposition'] as string)
    if (contentDisp && contentDisp.includes('filename')) {
      return contentDisp.split('=')[1]
    } else {
      console.error('Nombre del archivo no presente en content-disposition')
      throw new DownloadError(
        'Ha ocurrido un error al descargar el archivo',
        response.status
      )
    }
  }

  static downloadFile(
    data: BlobPart,
    filename: string,
    type: string,
    extension?: string
  ) {
    const blob = new Blob([data], { type })
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    document.body.appendChild(a)
    a.href = url
    a.download = extension ? filename + '.' + extension : filename
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  static printFile(data: BlobPart, type: string) {
    const blob = new Blob([data], { type })
    const url = window.URL.createObjectURL(blob)
    window.open(url)?.print()
  }

  static downloadFileDirect(url: string, filename?: string) {
    const link = document.createElement('a')
    link.href = url
    link.target = '_self'

    if (filename) {
      link.download = filename
    }
    link.click()
  }

  static downloadWithBackFilename(response: AxiosResponse) {
    if (response.status === HttpStatusCode.NoContent) {
      throw new DownloadError(
        'No se han encontrado archivos para la fecha seleccionada',
        response.status
      )
    }
    const filename = this.getFileName(response)
    if (filename) {
      this.downloadFile(
        response.data,
        filename,
        response.headers['content-type']
      )
    } else {
      throw new DownloadError(
        'Ha ocurrido un error al descargar el archivo',
        response.status
      )
    }
  }

  static downloadWithFilename(response: AxiosResponse, filename: string) {
    if (response.status === HttpStatusCode.NoContent) {
      throw new DownloadError(
        'No se han encontrado archivos para la fecha seleccionada',
        response.status
      )
    }
    this.downloadFile(response.data, filename, response.headers['content-type'])
  }

  static async handleDownload(
    requestPromise: Promise<AxiosResponse<File | BlobPart, any> | undefined>,
    downloadName: string,
    filetype: FileType,
    downloadType: DownloadType,
    request?: DownloadRequestParams,
    printWhenFinished?: boolean
  ) {
    const response = await requestPromise

    if (!response) {
      showToastError(
        newI18n.global.t('error'),
        newI18n.global.t('downloadFailedError')
      )
      return
    }
    const contentType: string =
      response.headers['content-type'] || response.headers['Content-Type']
    if (
      response.status === HttpStatusCode.Ok ||
      response.status === HttpStatusCode.Accepted
    ) {
      if (contentType && contentType.includes(MimeType.JSON)) {
        const reader = new FileReader()
        reader.onload = () => {
          let text = reader.result as
            | Stringified<GetMediaResponseDTO>
            | Stringified<GetVariablePDFMediaResponseDTO>
            | Stringified<GetMeteoReportDTO>

          if (typeof text === 'string') {
            const textParsed:
              | GetMediaResponseDTO
              | GetVariablePDFMediaResponseDTO
              | GetMeteoReportDTO = JSON.parse(text)

            if ('response' in textParsed) {
              useDownloadsStore().createDownload(
                textParsed.response.id,
                downloadName,
                downloadType,
                filetype,
                request
              )
            } else {
              if (
                downloadType === DownloadType.BULLETIN_PDF &&
                request &&
                'bulletinID' in request
              ) {
                request.downloadID = textParsed.id
              }

              useDownloadsStore().createDownload(
                textParsed.id,
                downloadName,
                downloadType,
                filetype,
                request,
                printWhenFinished
              )
            }
          }
        }
        reader.readAsText(response.data as Blob)
      } else {
        if (printWhenFinished) {
          this.printFile(response.data, FileType.getMimeType(filetype))
        } else {
          this.downloadFile(
            response.data,
            downloadName,
            FileType.getMimeType(filetype),
            FileType.getExtension(filetype) || response.headers['content-type']
          )
        }
      }
    } else {
      showToastError(
        newI18n.global.t('error'),
        newI18n.global.t('downloadFailedError')
      )
    }
  }
}
